import { useState, useEffect, useMemo } from "react";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, Package, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PRODUCT_CATALOG } from "@/constants/products";

interface StockControlProps {
  locationId: string;
  locationName: string;
  token: string;
}

interface ProductAvailability {
  product_id: string;
  is_available: boolean;
}

const StockControl = ({ locationId, locationName, token }: StockControlProps) => {
  const { toast } = useToast();
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [updatingProducts, setUpdatingProducts] = useState<Set<string>>(new Set());

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, typeof PRODUCT_CATALOG> = {};
    
    PRODUCT_CATALOG.forEach(product => {
      const category = product.category || "Otros";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(product);
    });
    
    return grouped;
  }, []);

  const categories = Object.keys(productsByCategory);

  // Fetch current availability for this location
  const fetchAvailability = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("product_availability")
        .select("product_id, is_available")
        .eq("location_id", locationId);

      if (error) throw error;

      // Convert to a map for easy lookup
      const availabilityMap: Record<string, boolean> = {};
      (data || []).forEach((item: ProductAvailability) => {
        availabilityMap[item.product_id] = item.is_available;
      });
      
      setAvailability(availabilityMap);
    } catch (error) {
      console.error("Error fetching availability:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la disponibilidad de productos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (locationId) {
      fetchAvailability();
    }
  }, [locationId]);

  // Check if product is available (default to true if no record exists)
  const isProductAvailable = (productId: string): boolean => {
    return availability[productId] ?? true;
  };

  // Count unavailable products
  const unavailableCount = useMemo(() => {
    return Object.values(availability).filter(v => v === false).length;
  }, [availability]);

  // Toggle product availability
  const toggleAvailability = async (productId: string, newValue: boolean) => {
    setUpdatingProducts(prev => new Set(prev).add(productId));
    
    try {
      const { error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "toggle-stock",
          token,
          productId,
          locationId,
          isAvailable: newValue,
        },
      });

      if (error) throw error;

      // Update local state optimistically
      setAvailability(prev => ({
        ...prev,
        [productId]: newValue,
      }));

      toast({
        title: newValue ? "✅ Disponible" : "🚫 Agotado",
        description: `Producto marcado como ${newValue ? "disponible" : "agotado"}`,
      });
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la disponibilidad",
        variant: "destructive",
      });
    } finally {
      setUpdatingProducts(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 text-primary" />
              <div>
                <h2 className="font-bold text-lg">Control de Stock</h2>
                <p className="text-sm text-muted-foreground">{locationName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unavailableCount > 0 && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {unavailableCount} agotados
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAvailability}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <Tabs defaultValue={categories[0]} className="w-full">
        <TabsList className="w-full flex overflow-x-auto gap-1 bg-muted/50 h-auto p-1 mb-4">
          {categories.map((category) => {
            const catProducts = productsByCategory[category];
            const catUnavailable = catProducts.filter(p => !isProductAvailable(p.id)).length;
            
            return (
              <TabsTrigger
                key={category}
                value={category}
                className="px-3 py-2 text-xs font-medium whitespace-nowrap flex-shrink-0 relative"
              >
                {category}
                {catUnavailable > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                  >
                    {catUnavailable}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categories.map((category) => (
          <TabsContent key={category} value={category} className="mt-0">
            <div className="grid gap-2">
              {productsByCategory[category].map((product) => {
                const available = isProductAvailable(product.id);
                const isUpdating = updatingProducts.has(product.id);
                
                return (
                  <Card 
                    key={product.id} 
                    className={`transition-all ${!available ? "bg-destructive/5 border-destructive/30" : ""}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-medium text-sm truncate ${!available ? "text-muted-foreground line-through" : ""}`}>
                              {product.name}
                            </h4>
                            {!available && (
                              <Badge variant="destructive" className="text-xs shrink-0">
                                AGOTADO
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {product.price.toFixed(2)}€
                            {product.subcategory && ` · ${product.subcategory}`}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {isUpdating && (
                            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          <Switch
                            checked={available}
                            onCheckedChange={(checked) => toggleAvailability(product.id, checked)}
                            disabled={isUpdating}
                            className={available ? "data-[state=checked]:bg-green-600" : ""}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default StockControl;
